import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, ArrowLeft } from "lucide-react";
import { mockPosts, featuredCreators, mockCreator } from "../../../data/mockData";
import PostCard from "../../../components/shared/PostCard";
import MerchCard from "./HomePage/components/creatorPreview/MerchCard";
import Button from "../../../components/ui/Button";

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const [activeTab, setActiveTab] = useState("all");
  const [results, setResults] = useState({ creators: [], posts: [], products: [] });

  useEffect(() => {
    if (!query) return;

    const lowerQuery = query.toLowerCase();

    const creators = featuredCreators.filter(
      (c) =>
        (c.username || "").toLowerCase().includes(lowerQuery) ||
        (c.displayName || "").toLowerCase().includes(lowerQuery) ||
        (c.category || "").toLowerCase().includes(lowerQuery)
    );

    const posts = mockPosts.filter(
      (p) =>
        (p.title || "").toLowerCase().includes(lowerQuery) ||
        (p.content || "").toLowerCase().includes(lowerQuery)
    );

    // Mock searching products from the mockCreator for now
    const products = mockCreator.merchandise.filter((p) =>
      (p.name || "").toLowerCase().includes(lowerQuery)
    );

    setResults({ creators, posts, products });
  }, [query]);

  const tabs = [
    { id: "all", label: "All" },
    { id: "creators", label: "Creators" },
    { id: "posts", label: "Posts" },
    { id: "products", label: "Products" },
  ];

  const renderCreators = () => (
    <div className="space-y-4">
      {results.creators.map((creator) => (
        <div
          key={creator.id}
          onClick={() => navigate(`/fan/creator/${creator.username}`)}
          className="flex items-center p-4 bg-white rounded-xl border border-slate-200 cursor-pointer hover:border-primary transition-colors"
        >
          <img
            src={creator.avatarUrl}
            alt={creator.displayName}
            className="w-12 h-12 rounded-full mr-4 object-cover"
          />
          <div className="flex-1">
            <h3 className="font-bold text-slate-800">{creator.displayName}</h3>
            <p className="text-sm text-slate-500">@{creator.username} • {creator.category}</p>
          </div>
          <Button variant="outline" size="sm">View</Button>
        </div>
      ))}
      {results.creators.length === 0 && (
        <p className="text-slate-500 text-center py-8">No creators found.</p>
      )}
    </div>
  );

  const renderPosts = () => (
    <div className="space-y-4">
      {results.posts.map((post) => (
        <PostCard key={post.id} post={post} author={featuredCreators[0]} />
      ))}
      {results.posts.length === 0 && (
        <p className="text-slate-500 text-center py-8">No posts found.</p>
      )}
    </div>
  );

  const renderProducts = () => (
    <div className="grid grid-cols-2 gap-4">
      {results.products.map((product) => (
        <MerchCard key={product.id} item={product} />
      ))}
      {results.products.length === 0 && (
        <p className="text-slate-500 text-center py-8 col-span-2">No products found.</p>
      )}
    </div>
  );

  return (
    <div className="pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-slate-100">
          <ArrowLeft size={20} className="text-slate-600" />
        </button>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            defaultValue={query}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                navigate(`/fan/search?q=${e.target.value}`);
              }
            }}
            className="w-full pl-9 pr-4 py-2 bg-slate-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto px-4 py-2 gap-2 no-scrollbar bg-white border-b border-slate-100">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? "bg-primary text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === "all" && (
          <div className="space-y-8">
            {results.creators.length > 0 && (
              <section>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="font-bold text-slate-800">Creators</h2>
                  <button onClick={() => setActiveTab("creators")} className="text-sm text-primary">See all</button>
                </div>
                {renderCreators()}
              </section>
            )}
            
            {results.products.length > 0 && (
              <section>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="font-bold text-slate-800">Products</h2>
                  <button onClick={() => setActiveTab("products")} className="text-sm text-primary">See all</button>
                </div>
                {renderProducts()}
              </section>
            )}

            {results.posts.length > 0 && (
              <section>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="font-bold text-slate-800">Posts</h2>
                  <button onClick={() => setActiveTab("posts")} className="text-sm text-primary">See all</button>
                </div>
                {renderPosts()}
              </section>
            )}

            {!results.creators.length && !results.posts.length && !results.products.length && (
               <div className="text-center py-12">
                 <Search size={48} className="mx-auto text-slate-300 mb-4" />
                 <h3 className="text-lg font-bold text-slate-700">No results found</h3>
                 <p className="text-slate-500">Try searching for something else</p>
               </div>
            )}
          </div>
        )}

        {activeTab === "creators" && renderCreators()}
        {activeTab === "posts" && renderPosts()}
        {activeTab === "products" && renderProducts()}
      </div>
    </div>
  );
};

export default SearchResultsPage;
